import { supabase } from "../supabase";

const FREE_STARTER_BONUS = 1;
export const NO_LISTING_CREDITS_ERROR = "NO_LISTING_CREDITS";

// Whitelist of emails that have unlimited listings (sponsored plan)
// Add emails here to grant them unlimited job listings
const UNLIMITED_LISTINGS_EMAILS = [
    // Add the 2 email addresses here that should have unlimited listings
    // Example: 'team@4mm.com',
    // Example: 'admin@example.com',
];

const getActiveListingCount = async (employerId) => {
    const { count, error } = await supabase
        .from("internal_job_listings")
        .select("*", { count: "exact", head: true })
        .eq("employer_id", employerId)
        .not("status", "eq", "archived");

    if (error) {
        throw error;
    }

    return count || 0;
};

const hasAwardedStarterBonus = async (employerId) => {
    const { data, error } = await supabase
        .from("job_listing_purchases")
        .select("id")
        .eq("employer_id", employerId)
        .eq("free_bonus_awarded", true)
        .limit(1);

    if (error) {
        throw error;
    }

    return (data || []).length > 0;
};

export const hasClaimedFreeStarterListing = async (employerId) => {
    if (!employerId) return true;
    return await hasAwardedStarterBonus(employerId);
};

export const recordJobListingPurchase = async ({
    employerId,
    listingCount,
    amountPaid,
    currency,
    paystackReference,
    paystackPayload,
    includeStarterBonus = false,
}) => {
    if (!employerId) {
        throw new Error("Employer ID is required to record a purchase.");
    }

    let bonusListings = 0;
    if (includeStarterBonus) {
        const alreadyAwarded = await hasAwardedStarterBonus(employerId);
        if (!alreadyAwarded) {
            bonusListings = FREE_STARTER_BONUS;
        }
    }

    const totalListingsAwarded = listingCount + bonusListings;

    const { data, error } = await supabase
        .from("job_listing_purchases")
        .insert([
            {
                employer_id: employerId,
                paid_listing_count: listingCount,
                listing_count: totalListingsAwarded,
                free_bonus_listings: bonusListings,
                free_bonus_awarded: bonusListings > 0,
                amount_paid: amountPaid,
                currency,
                paystack_reference: paystackReference,
                paystack_payload: paystackPayload,
                status: "paid",
            },
        ])
        .select()
        .single();

    if (error) {
        throw error;
    }

    return data;
};

export const fetchSponsoredEmployerIds = async () => {
    const { data, error } = await supabase
        .from("job_listing_purchases")
        .select("employer_id, paid_listing_count, status")
        .eq("status", "paid")
        .gt("paid_listing_count", 0);

    if (error) {
        throw error;
    }

    const employerIds = new Set(
        (data || [])
            .map((row) => row.employer_id)
            .filter(Boolean),
    );

    return Array.from(employerIds);
};

export const fetchListingPurchaseSummary = async (employerId) => {
    if (!employerId) {
        return {
            totalListings: 0,
            paidListings: 0,
            freeListings: 0,
        };
    }

    const { data, error } = await supabase
        .from("job_listing_purchases")
        .select("listing_count, paid_listing_count, free_bonus_listings")
        .eq("employer_id", employerId);

    if (error) {
        throw error;
    }

    const paidListings = (data || []).reduce(
        (sum, row) => sum + (row.paid_listing_count || 0),
        0,
    );
    const freeListings = (data || []).reduce(
        (sum, row) => sum + (row.free_bonus_listings || 0),
        0,
    );

    return {
        totalListings: paidListings + freeListings,
        paidListings,
        freeListings,
    };
};

export const fetchListingCreditUsage = async (employerId, userEmail = null) => {
    if (!employerId) {
        return {
            totalListings: FREE_STARTER_BONUS,
            paidListings: 0,
            freeListings: FREE_STARTER_BONUS,
            usedListings: 0,
            remainingListings: FREE_STARTER_BONUS,
            isFreePlan: true,
            starterAllowanceApplied: true,
        };
    }

    // Check if user email is in the unlimited listings whitelist
    const hasUnlimitedListings = userEmail && UNLIMITED_LISTINGS_EMAILS.includes(userEmail.toLowerCase().trim());
    
    if (hasUnlimitedListings) {
        // For unlimited accounts, return a high number to effectively bypass limits
        const activeListingCount = await getActiveListingCount(employerId);
        return {
            totalListings: 999999, // Effectively unlimited
            paidListings: 999999,
            freeListings: 0,
            usedListings: activeListingCount,
            remainingListings: 999999, // Always has remaining
            isFreePlan: false,
            starterAllowanceApplied: false,
        };
    }

    const [purchaseSummary, activeListingCount] = await Promise.all([
        fetchListingPurchaseSummary(employerId),
        getActiveListingCount(employerId),
    ]);

    const hasPaidPlan = purchaseSummary.totalListings > 0;
    const starterAllowance = hasPaidPlan ? 0 : FREE_STARTER_BONUS;
    const totalListings = purchaseSummary.totalListings + starterAllowance;
    const freeListings = purchaseSummary.freeListings + starterAllowance;
    const remainingListings = Math.max(totalListings - activeListingCount, 0);

    return {
        totalListings,
        paidListings: purchaseSummary.paidListings,
        freeListings,
        usedListings: activeListingCount,
        remainingListings,
        isFreePlan: !hasPaidPlan,
        starterAllowanceApplied: starterAllowance > 0,
    };
};

export const ensureListingCreditAvailable = async (employerId, userEmail = null) => {
    const usage = await fetchListingCreditUsage(employerId, userEmail);

    // Unlimited accounts always pass the check (remainingListings is 999999)
    if (usage.remainingListings <= 0) {
        const errorMessage = usage.isFreePlan
            ? "Your Free Starter job post is already live. Upgrade or purchase a plan to post more roles."
            : "You have used all of your purchased job listings. Please buy additional listings on the Pricing page.";
        const error = new Error(errorMessage);
        error.code = NO_LISTING_CREDITS_ERROR;
        error.usage = usage;
        throw error;
    }

    return usage;
};

